import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageCircle, Send, Reply, Sparkles } from "lucide-react";
import { API, auth, ghostBtn, handleAuthError, inputClass, fmtDate } from "./shared";
import { useSettings } from "@/context/SettingsContext";
import { DEFAULT_TEMPLATES, fillTemplate } from "./replyTemplates";

export const QuestionReplyBox = ({ q, i, token, logout, onDone }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [thinking, setThinking] = useState(false);
  const { settings } = useSettings();
  const suggest = async () => {
    setThinking(true);
    try {
      const r = await axios.post(`${API}/questions/${q.id}/suggest`, { draft: text }, auth(token));
      setText(r.data.suggestion);
      toast.success("Proposition rédigée par l'assistant IA : relisez et ajustez avant d'envoyer");
    } catch (err) {
      handleAuthError(err, logout, () => toast.error(err.response?.data?.detail || "Assistant IA indisponible"));
    } finally { setThinking(false); }
  };
  const templates = settings.content?.reply_templates?.length ? settings.content.reply_templates : DEFAULT_TEMPLATES;

  const send = async (channel) => {
    if (!text.trim()) { toast.error("Écrivez votre réponse d'abord"); return; }
    setSending(true);
    try {
      if (channel === "whatsapp") {
        window.open(`https://wa.me/${q.phone.replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Bonjour ${q.name},\n\n${text}\n\nGroupe Auto Luxe`)}`, "_blank", "noopener");
      }
      await axios.post(`${API}/questions/${q.id}/reply`, { message: text, channel }, auth(token));
      toast.success(channel === "email" ? `Réponse envoyée par e-mail à ${q.email}` : "Réponse enregistrée, WhatsApp ouvert");
      setText(""); setOpen(false); onDone();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error(err.response?.data?.detail || "Échec de l'envoi"));
    } finally { setSending(false); }
  };

  return (
    <div className="mt-4" data-testid={`admin-question-${i}-reply-box`}>
      {q.reply && (
        <div className="rounded-xl border border-brand/15 bg-mint/60 p-4 text-sm text-forest" data-testid={`admin-question-${i}-reply`}>
          <p className="font-mono text-[10px] uppercase tracking-widest text-brand">Votre réponse · {q.reply_channel === "email" ? "e-mail" : "WhatsApp"} · {fmtDate(q.replied_at)}</p>
          <p className="mt-2 whitespace-pre-line">{q.reply}</p>
        </div>
      )}
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid={`admin-question-${i}-reply-open`} className={`${ghostBtn} mt-3 border-gold text-gold`}><Reply className="h-3.5 w-3.5" /> {q.reply ? "Répondre à nouveau" : "Répondre au client"}</button>
      ) : (
        <div className="mt-3 rounded-2xl border border-gold/30 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2" data-testid={`admin-question-${i}-templates`}>
            <button type="button" disabled={thinking} onClick={suggest} data-testid={`admin-question-${i}-ai-suggest`} className="inline-flex items-center gap-1.5 rounded-full bg-forest px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white shadow transition-all hover:-translate-y-0.5 hover:bg-brand disabled:opacity-60"><Sparkles className="h-3.5 w-3.5 text-gold" /> {thinking ? "Rédaction en cours..." : text.trim() ? "Améliorer avec l'IA" : "Proposer une réponse (IA)"}</button>
            <span className="font-mono text-[10px] uppercase tracking-widest text-brand">Réponses types :</span>
            {templates.map((tpl, k) => (
              <button key={k} type="button" onClick={() => setText(fillTemplate(tpl.text, q.name))} data-testid={`admin-question-${i}-template-${k}`} className="rounded-full border border-brand/20 bg-mint/60 px-3 py-1 text-[11px] text-forest transition-colors hover:border-gold hover:bg-cream">{tpl.title}</button>
            ))}
          </div>
          <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} data-testid={`admin-question-${i}-reply-input`} placeholder={`Bonjour ${q.name}, ...`} className={`${inputClass} resize-y`} />
          <div className="mt-3 flex flex-wrap gap-2">
            {q.email && <button disabled={sending} onClick={() => send("email")} data-testid={`admin-question-${i}-reply-email`} className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white shadow-lg shadow-gold/25 transition-all hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-60"><Mail className="h-3.5 w-3.5" /> {sending ? "Envoi..." : `Envoyer par e-mail`}</button>}
            {q.phone && <button disabled={sending} onClick={() => send("whatsapp")} data-testid={`admin-question-${i}-reply-whatsapp`} className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white shadow-lg shadow-brand/25 transition-all hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-60"><MessageCircle className="h-3.5 w-3.5" /> Envoyer via WhatsApp</button>}
            <button onClick={() => setOpen(false)} data-testid={`admin-question-${i}-reply-cancel`} className={ghostBtn}>Annuler</button>
          </div>
          {!q.email && <p className="mt-2 text-xs text-brand-hover">Ce client n'a pas laissé d'e-mail : la réponse sera envoyée via WhatsApp.</p>}
        </div>
      )}
    </div>
  );
};
